import { Injectable } from '@angular/core';
import { BaseService, HubNetCore, ReConnectBase, ReConnectType } from '@hunchads/angular-signalr';
import { throttleTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

import { get, set } from './global.service';
import { MessageHub } from '../models';
import { CatalogsHubCommands, CatalogsHubEvents } from '../const';
import { ConfigService } from './config.service';

@Injectable({
    providedIn: 'root'
})
export class CatalogsHubService extends BaseService<CatalogsHub> {
    public messageState: Subject<MessageHub> = new Subject();
    constructor(
        catalogsHub: CatalogsHub //
    ) {
        super(catalogsHub);
        set('accountIdsMap', []);
        set('catalogIdsMap', []);
    }

    public isConnected(): boolean {
        return this.hub.isConnected();
    }

    public getConnectionId(): string | null {
        return (this.hub as any).getConnectionId?.() || null;
    }

    registerByAccountId(accountId: string): void {
        this.hub.invoke(CatalogsHubCommands.REGISTER_BY_ACCOUNT_ID, undefined, accountId);
        const idsMap: string[] = get('accountIdsMap');
        if (!idsMap.includes(accountId)) {
            idsMap.push(accountId);
            set('accountIdsMap', idsMap);
        }
        console.log(`Register by Account Id ${accountId}`);
    }

    registerByCatalogId(catalogId: string): void {
        this.hub.invoke(CatalogsHubCommands.REGISTER_BY_CATALOG_ID, () => { }, catalogId);
        const idsMap: string[] = get('catalogIdsMap');
        if (!idsMap.includes(catalogId)) {
            idsMap.push(catalogId);
            set('catalogIdsMap', idsMap);
        }
        console.log(`Register by Catalog Id ${catalogId}`);
    }

    unRegisterByAccountId(accountId: string): void {
        this.hub.invoke(CatalogsHubCommands.UNREGISTER_BY_ACCOUNT_ID, () => { }, accountId);
        const idsMap: string[] = get('accountIdsMap');
        if (idsMap.includes(accountId)) {
            const index = idsMap.findIndex(id => id === accountId);
            idsMap.splice(index, 1);
            set('accountIdsMap', idsMap);
        }
    }

    unRegisterByCatalogId(catalogId: string): void {
        this.hub.invoke(CatalogsHubCommands.UNREGISTER_BY_CATALOG_ID, () => { }, catalogId);
        const idsMap: string[] = get('catalogIdsMap');
        if (idsMap.includes(catalogId)) {
            const index = idsMap.findIndex(id => id === catalogId);
            idsMap.splice(index, 1);
            set('catalogIdsMap', idsMap);
        }
    }

    listenHubSubscriptions(): void {
        this.hub.subscribe(CatalogsHubEvents.INPUT_SYNC_STARTED, message => {
            console.log(CatalogsHubEvents.INPUT_SYNC_STARTED, message);
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.INPUT_SYNC_STARTED }));
        });
        this.hub.subscribe(CatalogsHubEvents.INPUT_SYNC_FINISHED, message => {
            console.log(CatalogsHubEvents.INPUT_SYNC_FINISHED, message);
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.INPUT_SYNC_FINISHED }));
        });
        this.hub.subscribe(CatalogsHubEvents.CREATE_OUTPUT_STARTED, message => {
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.CREATE_OUTPUT_STARTED }));
        });
        this.hub.subscribe(CatalogsHubEvents.CREATE_OUTPUT_FINISHED, message => {
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.CREATE_OUTPUT_FINISHED }));
        });
        this.hub.subscribe(CatalogsHubEvents.PHASE_COMPLETED, message => {
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.PHASE_COMPLETED }));
        });
        this.hub.subscribe(CatalogsHubEvents.CATALOG_SYNC_FINISHED, message => {
            console.log(CatalogsHubEvents.CATALOG_SYNC_FINISHED, message);
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.CATALOG_SYNC_FINISHED }));
        });
        this.hub.subscribe(CatalogsHubEvents.CATALOG_TEMPLATE_CHANGE, message => {
            console.log(CatalogsHubEvents.CATALOG_TEMPLATE_CHANGE, message);
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.CATALOG_TEMPLATE_CHANGE }));
        });
        this.hub.subscribe(CatalogsHubEvents.CREATIVE_PRODUCTION_READY, message => {
            console.log(CatalogsHubEvents.CREATIVE_PRODUCTION_READY, message);
            this.messageState.next(new MessageHub({ message: JSON.parse(message), type: CatalogsHubEvents.CREATIVE_PRODUCTION_READY }));
        });
    }
}

@Injectable({
    providedIn: 'root'
})
export class CatalogsHub extends HubNetCore {
    private currentConnectionId: string | null = null;
    private subscriptions: Subscription[] = [];

    constructor(private configService: ConfigService) {
        super('catalogshub', get('signalRUrl'));

        // Handle reconnection for unauthorized
        const reconnectSub = this.reconnect.asObservable().subscribe(async (value: ReConnectBase) => {
            if (value.type === ReConnectType.UNAUTHORIZED) {
                await this.configService.loadHubData();
            }
        });
        this.subscriptions.push(reconnectSub);

        // Handle connection established - check if connection ID changed
        const connectionSub = this.onConnectionEstablished.asObservable().pipe(
            throttleTime(300, undefined, { leading: true, trailing: true }),
            map(data => data.connectionId),
            distinctUntilChanged()
        ).subscribe(async (newConnectionId: string | undefined) => {
            if (!newConnectionId) {
                console.warn('[CatalogsHub] No connection ID received');
                return;
            }

            console.log('[CatalogsHub] Connection established with ID:', newConnectionId);

            // Ensure token is valid before re-registering
            await this.configService.ensureValidSignalRToken();

            // Check if connection ID has changed
            if (this.currentConnectionId && this.currentConnectionId !== newConnectionId) {
                console.log(`[CatalogsHub] Connection ID changed from ${this.currentConnectionId} to ${newConnectionId}`);
                console.log('[CatalogsHub] Unregistering old subscriptions and re-registering with new connection...');

                // Unregister all IDs from old connection first
                this.unsubscribeConnectionIdSubscriptions(this.currentConnectionId);
            }

            // Update current connection ID
            this.currentConnectionId = newConnectionId;

            // Register all IDs with new connection
            this.registerAllIds();
        });
        this.subscriptions.push(connectionSub);
    }

    private unsubscribeConnectionIdSubscriptions(connectionId: string): void {
        const catalogIds = (get('catalogIdsMap') || []) as string[];
        const accountIds = (get('accountIdsMap') || []) as string[];

        console.log(`[CatalogsHub] Unregistering ${catalogIds.length} catalogs, ${accountIds.length} accounts from connection ${connectionId}`);

        if (catalogIds.length > 0) {
            catalogIds.forEach((id: string) => {
                this.invoke(CatalogsHubCommands.UNREGISTER_RETRO_BY_CATALOG_ID, () => { }, id, connectionId);
            });
        }

        if (accountIds.length > 0) {
            accountIds.forEach((id: string) => {
                this.invoke(CatalogsHubCommands.UNREGISTER_RETRO_BY_ACCOUNT_ID, () => { }, id, connectionId);
            });
        }
    }

    private registerAllIds(): void {
        const catalogIds = (get('catalogIdsMap') || []) as string[];
        const accountIds = (get('accountIdsMap') || []) as string[];

        console.log(`[CatalogsHub] Registering ${catalogIds.length} catalogs, ${accountIds.length} accounts`);

        if (catalogIds.length > 0) {
            catalogIds.forEach((id: string) => {
                this.invoke(CatalogsHubCommands.REGISTER_BY_CATALOG_ID, () => { }, id);
            });
        }

        if (accountIds.length > 0) {
            accountIds.forEach((id: string) => {
                this.invoke(CatalogsHubCommands.REGISTER_BY_ACCOUNT_ID, () => { }, id);
            });
        }
    }

    override ngOnDestroy(): void {
        console.log('[CatalogsHub] Cleaning up subscriptions...');
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
        super.ngOnDestroy();
    }

    override getToken() {
        return get('signalRAccessToken');
    }

    public getConnectionId(): string | null {
        return this.currentConnectionId;
    }
}
